import React, { useEffect } from 'react';
import { Box, Button, Divider } from '@ublend-npm/aulaui-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ConversationCardSkeleton from './ConversationCardSkeleton';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { useDispatch } from 'react-redux';
import { WidgetCard, WidgetErrorState } from '../common';
import ConversationListCard from '@app/components/presentational/MessagingResponsive/ConversationListCard';
import WidgetEmptyState from '../common/WidgetEmptyState';
import { NAVIGATION_SOURCE_MESSAGES } from '@core/constants/analytics';
import { trackHomeViewAllClicked } from '../common';

const ConversationListCardSkeletons = () => {
  return (
    <>
      {Array.from({ length: 4 }).map((_, index) => (
        <Box key={index} mt={1}>
          <ConversationCardSkeleton />
        </Box>
      ))}
    </>
  );
};

interface MessageWidgetCardProps {
  handleConversationClick: (id: string) => void;
  setIsAllConversationsDialogOpen: (isOpen: boolean) => void;
  conversations: any[];
  totalUnreadCount: number;
  mutedConversation: any;
}

const MessageWidgetCard = (props: MessageWidgetCardProps) => {
  const {
    handleConversationClick,
    setIsAllConversationsDialogOpen,
    conversations,
    totalUnreadCount,
    mutedConversation,
  } = props;

  const limitedConversations = conversations.slice(0, 4);
  const [isLoading, setIsLoading] = React.useState(true);
  const errorType = null;
  const dispatch = useDispatch<any>();

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference('home-messages-widget', true));
  };

  const menuActions = [{ label: 'Hide', onClick: handleHideWidget }];

  useEffect(() => {
    if (limitedConversations.length > 0 || errorType) {
      setIsLoading(false);
    }
  }, [limitedConversations, errorType]);

  const footer =
    conversations.length > 4 ? (
      <Button
        fullWidth
        kind="secondary"
        endIcon={<ArrowForwardIcon />}
        onClick={() => {
          trackHomeViewAllClicked(
            NAVIGATION_SOURCE_MESSAGES,
            conversations.length
          );
          setIsAllConversationsDialogOpen(true);
        }}
      >
        View all messages
      </Button>
    ) : undefined;

  return (
    <WidgetCard
      title={'Messages'}
      badgeCount={totalUnreadCount}
      badgeMessage={`${totalUnreadCount} unread messages`}
      footer={footer}
      menuActions={menuActions}
    >
      <Box>
        {isLoading ? (
          <ConversationListCardSkeletons />
        ) : errorType ? (
          <WidgetErrorState
            type={errorType}
            onRetry={() => window.location.reload()}
            isRetrying={isLoading}
          />
        ) : limitedConversations.length > 0 ? (
          limitedConversations.map((conversation, index) => (
            <Box key={conversation?.id}>
              <ConversationListCard
                conversation={conversation}
                conversationMuted={!!mutedConversation?.[conversation.id]}
                selected={false}
                onClick={() => {
                  handleConversationClick(conversation?.id);
                }}
              />
              {index !== 3 && <Divider sx={{ marginBlock: 1 }} />}
            </Box>
          ))
        ) : (
          <WidgetEmptyState
            emptyTitle="No new messages"
            emptySubtitle="You don't have any unread messages."
          />
        )}
      </Box>
    </WidgetCard>
  );
};

export default MessageWidgetCard;
